/**
 * Finds the highlightText in the container and highlights it.
 * @param containerId
 * @param highlightText
 */
export function findAndHighlightText(containerId: string, highlightText?: string, highlightClassName = "special-highlight") {
    if (!highlightText) return

    let container = document.getElementById(containerId)

    if (!container) return

    let fullText = (container.textContent ?? "").replace(/\u00A0/g, " ")

    let escapedText = highlightText
        .trim()
        .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

    let normalizedPattern = escapedText.replace(/\s+/g, "\\s*")
    let regex = new RegExp(normalizedPattern, "i")
    let match = regex.exec(fullText)

    if (!match) return

    let targetIndex = match.index
    let targetEnd = targetIndex + match[0].length

    let walker = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT
    )

    let charsSeen = 0

    let startNode: Text | null = null
    let startOffset = 0

    let endNode: Text | null = null
    let endOffset = 0

    while (walker.nextNode()) {
        let node = walker.currentNode as Text
        let nodeText = node.data
        let nodeStart = charsSeen
        let nodeEnd = charsSeen + nodeText.length

        if (!startNode &&
            targetIndex >= nodeStart &&
            targetIndex < nodeEnd) {

            startNode = node
            startOffset = targetIndex - nodeStart
        }

        if (startNode &&
            targetEnd > nodeStart &&
            targetEnd <= nodeEnd) {

            endNode = node
            endOffset = targetEnd - nodeStart
            break
        }

        charsSeen = nodeEnd
    }

    if (!startNode || !endNode) return

    let range = document.createRange()
    range.setStart(startNode, startOffset)
    range.setEnd(endNode, endOffset)

    let wrapper = document.createElement("span")
    wrapper.className = highlightClassName

    try {
        range.surroundContents(wrapper)
    }
    catch {
        let contents = range.extractContents()
        wrapper.appendChild(contents)
        range.insertNode(wrapper)
    }
}

/**
 * Clear the highlight in the container
 * @param containerElementOrId 
 */
export function clearHighlights(containerElementOrId: HTMLElement | string, highlightClassName = "special-highlight") {
    if (typeof containerElementOrId === "string") {
        containerElementOrId = document.getElementById(containerElementOrId) as HTMLElement
    }

    if (!containerElementOrId) return

    let highlights = containerElementOrId.querySelectorAll(`.${highlightClassName}`)

    highlights.forEach(highlight => {
        let fragment = document.createDocumentFragment()

        while (highlight.firstChild) {
            fragment.appendChild(highlight.firstChild)
        }

        highlight.replaceWith(fragment)
    })
}

/**
 * Scrolls to the anchor point in the scollable container
 * @param containerId 
 * @param anchorStr 
 * @returns 
 */
export function scrollToAnchor(containerId: string, anchorStr?: string) {
    if (!anchorStr) return
    let container = document.getElementById(containerId)
    if (!container) return

    let normalizedAnchor = anchorStr.trim()
    if (!normalizedAnchor) return

    let escapeValue = (value: string) => {
        if (window.CSS && CSS.escape) return CSS.escape(value)
        return value.replace(/"/g, "\\\"")
    }

    let selector = `[id="${escapeValue(normalizedAnchor)}"], [name="${escapeValue(normalizedAnchor)}"]`
    let anchorElement = container.querySelector(selector)
    if (anchorElement) {
        let containerRect = container.getBoundingClientRect()
        let targetRect = anchorElement.getBoundingClientRect()
        let offset = targetRect.top - containerRect.top
        container.scrollTo({
            top: container.scrollTop + offset - container.clientHeight / 3,
            behavior: "smooth",
        })
        return
    }

    let candidates = [normalizedAnchor]
    if (/^\d+$/.test(normalizedAnchor)) {
        candidates.push(`[${normalizedAnchor}]`, `(${normalizedAnchor})`)
    }

    let fullText = container.textContent ?? ""
    let matchIndex = -1
    let matchText = ""
    for (let candidate of candidates) {
        let idx = fullText.indexOf(candidate)
        if (idx !== -1) {
            matchIndex = idx
            matchText = candidate
            break
        }
    }
    if (matchIndex === -1) return

    let walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
    let charsSeen = 0
    let startNode: Text | null = null
    let startOffset = 0
    let endNode: Text | null = null
    let endOffset = 0
    let targetEnd = matchIndex + matchText.length

    while (walker.nextNode()) {
        let node = walker.currentNode as Text
        let nodeText = node.data
        let nodeStart = charsSeen
        let nodeEnd = charsSeen + nodeText.length

        if (!startNode && matchIndex >= nodeStart && matchIndex < nodeEnd) {
            startNode = node
            startOffset = matchIndex - nodeStart
        }
        if (startNode && targetEnd <= nodeEnd) {
            endNode = node
            endOffset = targetEnd - nodeStart
            break
        }
        charsSeen = nodeEnd
    }

    if (!startNode || !endNode) return

    let range = document.createRange()
    range.setStart(startNode, startOffset)
    range.setEnd(endNode, endOffset)

    let rect = range.getBoundingClientRect()
    let containerRect = container.getBoundingClientRect()
    let offset = rect.top - containerRect.top
    container.scrollTo({
        top: container.scrollTop + offset - container.clientHeight / 3,
        behavior: "smooth",
    })
}

/**
 * Replace the $CDN with the proper URL for images in the HTML content
 * 
 * @param BaseUrlCDN Base URL for the CDN (Content Delivery Network)
 * @param htmlStr Main content that may have $CDN key
 * @param className optional class name for the newly created image in the content
 * @returns Fully populated content with images (if any)
 */
export function prepareContentWithCDNImages(BaseUrlCDN: string, htmlStr?: string, className?: string) {
    if (!htmlStr) return ""
    if (!htmlStr.includes("$CDN")) return htmlStr

    let cdnImagePattern = /\$CDN\/[^\s<>"]+\.(?:png|jpe?g|gif|svg)/gi
    let withImages = htmlStr.replace(cdnImagePattern, (match) => {
        let url = match.replace("$CDN", BaseUrlCDN)
        return `<img src="${url}" class="${className}" alt="question image"/>`
    })

    return withImages.replace(/\$CDN/g, BaseUrlCDN)
}

/**
 * Returns layout and hierarchy information for a DOM element,
 * including its bounding rect, parent element, and offset parent.
 *
 * @param element - The target HTMLElement
 * @returns An object containing element, parent, and offsetParent info,
 *          or null if the element is undefined
 */
export function getElementLayoutInfo(element: HTMLElement) {
    if (!element) return null

    let parent = element.parentElement
    let offsetParent = element.offsetParent as HTMLElement | null
    let data = {
        element: {
            id: element.id,
            tagName: element.tagName,
            className: element.className,
            rect: element.getBoundingClientRect(),
        },
        parent: parent ? {
            id: parent.id,
            tagName: parent.tagName,
            className: parent.className,
            rect: parent.getBoundingClientRect(),
        } : null,
        offsetParent: offsetParent ? {
            id : offsetParent.id,
            tagName: offsetParent.tagName,
            className: offsetParent.className,
            rect: offsetParent.getBoundingClientRect(),
        } : null,
    }

    return data
}